import { useState, useRef, useEffect } from 'react';
import { Clock } from '@phosphor-icons/react';

/**
 * Custom TimePicker component with full theme support
 * Replaces native <input type="time"> which doesn't respect theme colors
 */
export default function TimePicker({ 
  value = '09:00', 
  onChange, 
  disabled = false,
  className = '',
  error = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const hoursListRef = useRef(null);
  const minutesListRef = useRef(null);
  
  // Parse current value
  const [hours, minutes] = value ? value.split(':').map(Number) : [9, 0];
  
  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  
  // Scroll to selected time when opening
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure refs are available
      setTimeout(() => {
        if (hoursListRef.current) {
          const selectedHour = hoursListRef.current.querySelector('[data-selected="true"]');
          if (selectedHour) {
            selectedHour.scrollIntoView({ block: 'center' });
          }
        }
        if (minutesListRef.current) {
          const selectedMinute = minutesListRef.current.querySelector('[data-selected="true"]');
          if (selectedMinute) {
            selectedMinute.scrollIntoView({ block: 'center' });
          }
        }
      }, 50);
    }
  }, [isOpen]);
  
  // Close on Escape
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);
  
  function handleTimeChange(newHours, newMinutes) {
    const h = String(newHours).padStart(2, '0');
    const m = String(newMinutes).padStart(2, '0');
    onChange(`${h}:${m}`);
  }
  
  function formatDisplayTime(value) {
    if (!value) return '09:00';
    const [h, m] = value.split(':');
    return `${h}:${m}`;
  }
  
  // Generate hours (0-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => i);
  
  // Generate minutes (0, 5, 10, 15, ... 55)
  const minuteOptions = Array.from({ length: 12 }, (_, i) => i * 5);
  
  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`input w-full text-left flex items-center justify-between gap-2 ${error ? 'input-error' : ''}`}
        style={{ 
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
        aria-invalid={error ? 'true' : 'false'}
      >
        <span style={{ color: 'var(--color-text-primary)' }}>
          {formatDisplayTime(value)}
        </span>
        <Clock size={18} style={{ color: 'var(--color-text-muted)' }} />
      </button>
      
      {/* Dropdown time picker */}
      {isOpen && (
        <div 
          className="absolute z-50 mt-1 rounded-sm shadow-lg overflow-hidden"
          style={{
            backgroundColor: 'var(--color-bg-canvas)',
            border: '1px solid var(--color-border-subtle)',
            width: '160px',
          }}
        >
          {/* Header */}
          <div 
            className="px-3 py-2 text-center text-sm font-medium"
            style={{ 
              backgroundColor: 'var(--color-bg-surface)',
              borderBottom: '1px solid var(--color-border-subtle)',
              color: 'var(--color-text-secondary)',
            }}
          >
            Select Time
          </div>
          
          {/* Time columns */}
          <div className="flex">
            {/* Hours column */}
            <div 
              ref={hoursListRef}
              className="flex-1 max-h-48 overflow-y-auto"
              style={{ borderRight: '1px solid var(--color-border-subtle)' }}
            >
              {hourOptions.map((hour) => {
                const isSelected = hour === hours;
                return (
                  <button
                    key={hour}
                    type="button"
                    data-selected={isSelected}
                    onClick={() => handleTimeChange(hour, minutes)}
                    className="w-full py-2 text-sm text-center transition-colors"
                    style={{
                      backgroundColor: isSelected 
                        ? 'var(--color-brand-primary)' 
                        : 'transparent',
                      color: isSelected 
                        ? 'white' 
                        : 'var(--color-text-primary)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = 'var(--color-bg-raised)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {String(hour).padStart(2, '0')}
                  </button>
                );
              })}
            </div>
            
            {/* Minutes column */}
            <div 
              ref={minutesListRef}
              className="flex-1 max-h-48 overflow-y-auto"
            >
              {minuteOptions.map((minute) => {
                const isSelected = minute === minutes || 
                  (minutes !== 0 && minute < minutes && minuteOptions[minuteOptions.indexOf(minute) + 1] > minutes);
                const exactMatch = minute === minutes;
                
                return (
                  <button
                    key={minute}
                    type="button"
                    data-selected={exactMatch}
                    onClick={() => handleTimeChange(hours, minute)}
                    className="w-full py-2 text-sm text-center transition-colors"
                    style={{
                      backgroundColor: exactMatch 
                        ? 'var(--color-brand-primary)' 
                        : 'transparent',
                      color: exactMatch 
                        ? 'white' 
                        : 'var(--color-text-primary)',
                    }}
                    onMouseEnter={(e) => {
                      if (!exactMatch) {
                        e.target.style.backgroundColor = 'var(--color-bg-raised)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!exactMatch) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {String(minute).padStart(2, '0')}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Current time display */}
          <div 
            className="px-3 py-2 text-center text-sm font-medium"
            style={{ 
              backgroundColor: 'var(--color-bg-surface)',
              borderTop: '1px solid var(--color-border-subtle)',
              color: 'var(--color-text-primary)',
            }}
          >
            {formatDisplayTime(value)}
          </div>
        </div>
      )}
    </div>
  );
}
